/**
 * Subscription Success Page
 *
 * Landing page after successful Stripe checkout.
 * Loads the subscription from database and redirects to the subscription page.
 */

import React, { useEffect, useState } from 'react';
import { View, Text, ActivityIndicator } from 'react-native';
import { useRouter } from 'expo-router';
import { Check } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { track, useTrackScreen } from '@/lib/analytics';
import { useSubscriptionStore } from '@/lib/state/subscription-store';
import { useProfileStore } from '@/lib/state/profile-store';
import { reportError } from '@/lib/errors';

export default function SubscriptionSuccessScreen() {
  useTrackScreen('SubscriptionSuccess');
  const router = useRouter();
  const colors = useThemeColors();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');

  const loadSubscription = useSubscriptionStore((s) => s.loadSubscription);
  const userRole = useProfileStore((s) => s.userRole);

  // Track subscription started on mount (ref-guarded)
  const subscriptionTracked = React.useRef(false);
  useEffect(() => {
    if (subscriptionTracked.current) return;
    subscriptionTracked.current = true;
    track('subscription_started');
  }, []);

  useEffect(() => {
    const handleSuccess = async () => {
      try {
        // Load the subscription from the database
        await loadSubscription();
        setStatus('success');

        // Wait a moment to show success message, then redirect
        setTimeout(() => {
          const subscriptionPath = userRole === 'investor'
            ? '/(investor)/investor-subscription'
            : '/(startup)/startup-subscription';
          router.replace(subscriptionPath);
        }, 2000);
      } catch (err) {
        reportError(err, { context: 'SubscriptionSuccess.handleSuccess' });
        setStatus('error');
        // Still redirect after error
        setTimeout(() => {
          router.replace('/');
        }, 3000);
      }
    };

    handleSuccess();
  }, [loadSubscription, router, userRole]);

  return (
    <View
      style={{
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: colors.surface.primary,
        padding: 24,
      }}
    >
      {status === 'loading' && (
        <>
          <ActivityIndicator size="large" color={colors.primary[600]} />
          <Text
            style={{
              marginTop: 16,
              fontSize: 18,
              fontWeight: '600',
              color: colors.text.primary,
            }}
          >
            Processing your subscription...
          </Text>
        </>
      )}

      {status === 'success' && (
        <>
          <View
            style={{
              width: 80,
              height: 80,
              borderRadius: 40,
              backgroundColor: colors.traffic.greenLight,
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: 24,
            }}
          >
            <Check size={40} color={colors.success} />
          </View>
          <Text
            style={{
              fontSize: 24,
              fontWeight: '700',
              color: colors.text.primary,
              marginBottom: 8,
            }}
          >
            Subscription Updated!
          </Text>
          <Text
            style={{
              fontSize: 16,
              color: colors.text.secondary,
              textAlign: 'center',
            }}
          >
            Your changes have been saved. Redirecting...
          </Text>
        </>
      )}

      {status === 'error' && (
        <>
          <Text
            style={{
              fontSize: 18,
              fontWeight: '600',
              color: colors.text.primary,
              marginBottom: 8,
            }}
          >
            Something went wrong
          </Text>
          <Text
            style={{
              fontSize: 14,
              color: colors.text.secondary,
              textAlign: 'center',
            }}
          >
            Don't worry, your payment was processed. Redirecting...
          </Text>
        </>
      )}
    </View>
  );
}
